// Display formatters. Money arrives as integer cents (+ currency) and provider
// cost as integer micro-USD; no float arithmetic is done on stored values
// beyond the final division for display. Numbers and currency always use
// en-US grouping (money inputs are parsed with a '.' decimal); only dates and
// relative times follow the UI locale.
import type { UiLocale } from './i18n';

const EMPTY = '—';

const moneyFormatters = new Map<string, Intl.NumberFormat>();
function moneyFormatter(currency: string, compact: boolean): Intl.NumberFormat {
  const key = `${currency}:${compact}`;
  let fmt = moneyFormatters.get(key);
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: compact ? 0 : 2,
        minimumFractionDigits: compact ? 0 : 2,
      });
    } catch {
      fmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    moneyFormatters.set(key, fmt);
  }
  return fmt;
}

export function money(cents: number | null | undefined, currency = 'USD', opts: { compact?: boolean } = {}): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return EMPTY;
  return moneyFormatter(currency || 'USD', Boolean(opts.compact)).format(cents / 100);
}

/** Provider cost in micro-USD (1 USD = 1_000_000). */
export function microUsd(micros: number | null | undefined): string {
  if (micros === null || micros === undefined || !Number.isFinite(micros)) return EMPTY;
  const usd = micros / 1_000_000;
  const digits = Math.abs(usd) < 1 ? 4 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(usd);
}

export function num(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
}

/** Ratio in [0,1] shown as percent. Values > 1 are assumed to already be percents. */
export function pct(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return EMPTY;
  const value = ratio > 1 ? ratio : ratio * 100;
  return `${value.toFixed(digits).replace(/\.0+$/, '')}%`;
}

export function multiple(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)}×`;
}

export function duration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return EMPTY;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const m = Math.floor(s / 60);
  const rest = Math.round(s % 60);
  return `${m}m ${rest.toString().padStart(2, '0')}s`;
}

export function bytes(size: number | null | undefined): string {
  if (size === null || size === undefined || !Number.isFinite(size)) return EMPTY;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATE_STYLES: Record<UiLocale, { date: Intl.DateTimeFormatOptions; short: Intl.DateTimeFormatOptions; time: Intl.DateTimeFormatOptions }> = {
  en: {
    date: { month: 'short', day: 'numeric', year: 'numeric' },
    short: { month: 'short', day: 'numeric' },
    time: { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  },
  // Brazilian business convention: numeric day/month, 24-hour clock.
  'pt-BR': {
    date: { day: '2-digit', month: '2-digit', year: 'numeric' },
    short: { day: '2-digit', month: '2-digit' },
    time: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
  },
};

function intlLocale(locale: UiLocale): string {
  return locale === 'pt-BR' ? 'pt-BR' : 'en-US';
}

export function date(value: string | Date | null | undefined, locale: UiLocale = 'en'): string {
  const d = parseDate(value);
  if (!d) return EMPTY;
  return d.toLocaleDateString(intlLocale(locale), DATE_STYLES[locale].date);
}

export function shortDate(value: string | Date | null | undefined, locale: UiLocale = 'en'): string {
  const d = parseDate(value);
  if (!d) return EMPTY;
  return d.toLocaleDateString(intlLocale(locale), DATE_STYLES[locale].short);
}

export function dateTime(value: string | Date | null | undefined, locale: UiLocale = 'en'): string {
  const d = parseDate(value);
  if (!d) return EMPTY;
  return d.toLocaleString(intlLocale(locale), DATE_STYLES[locale].time);
}

/** "Sep 30" for a YYYY-MM-DD calendar day, shown as is (no time-zone shift). Never the browser's locale. */
export function calendarDay(ymd: string | null | undefined, locale: UiLocale = 'en'): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? '');
  if (!m) return EMPTY;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).toLocaleDateString(intlLocale(locale), { ...DATE_STYLES[locale].short, timeZone: 'UTC' });
}

export function relative(value: string | Date | null | undefined, locale: UiLocale = 'en'): string {
  const d = parseDate(value);
  if (!d) return EMPTY;
  const diff = Date.now() - d.getTime();
  const abs = Math.abs(diff);
  const past = diff >= 0;
  if (locale === 'pt-BR') {
    const ago = (amount: string) => (past ? `há ${amount}` : `em ${amount}`);
    if (abs < 60_000) return past ? 'agora mesmo' : 'em instantes';
    if (abs < 3_600_000) return ago(`${Math.round(abs / 60_000)} min`);
    if (abs < 86_400_000) return ago(`${Math.round(abs / 3_600_000)} h`);
    if (abs < 30 * 86_400_000) {
      const days = Math.round(abs / 86_400_000);
      return ago(`${days} ${days === 1 ? 'dia' : 'dias'}`);
    }
    return date(d, locale);
  }
  const suffix = past ? 'ago' : 'from now';
  if (abs < 60_000) return past ? 'just now' : 'in a moment';
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)} min ${suffix}`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)} h ${suffix}`;
  if (abs < 30 * 86_400_000) return `${Math.round(abs / 86_400_000)} d ${suffix}`;
  return date(d);
}

export function shortId(id: string | null | undefined): string {
  if (!id) return EMPTY;
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

export function orderLabel(order: { number?: string | number | null; externalId?: string | null; id: string }): string {
  if (order.number !== null && order.number !== undefined && order.number !== '') {
    const n = String(order.number);
    return n.startsWith('#') ? n : `#${n}`;
  }
  if (order.externalId) return order.externalId;
  return shortId(order.id);
}

export function titleCase(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const s = value.replace(/[_-]+/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function errorText(error: unknown): string | null {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error && 'message' in error && typeof (error as { message: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  return 'Unknown error';
}

/** Dollars/decimal string → integer cents without float drift. */
export function parseMoneyToCents(input: string): number | null {
  const s = input.trim().replace(/[,\s$]/g, '');
  if (!s) return null;
  if (!/^-?\d+(\.\d{0,2})?$/.test(s)) return Number.NaN;
  const negative = s.startsWith('-');
  const [whole, frac = ''] = s.replace('-', '').split('.');
  const cents = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
  return negative ? -cents : cents;
}

export function centsToInput(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '';
  const negative = cents < 0;
  const abs = Math.abs(Math.trunc(cents));
  const out = `${Math.floor(abs / 100)}.${(abs % 100).toString().padStart(2, '0')}`;
  return negative ? `-${out}` : out;
}

export function currencySymbol(currency = 'USD'): string {
  try {
    const parts = new Intl.NumberFormat('en-US', { style: 'currency', currency }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}
